import { useCallback } from "react";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper.ts";
import { useCreateVmetaField } from "@via/pages/projects/metadata/metadata.api.tsx";
import { useGetProjectDatasetFields } from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";

interface UseFieldManagementOptions {
  selectedVmetaProject: SelectOption | null;
  collectionId: string;
}

interface UseFieldManagementReturn {
  createFields: (fieldsToCreate: FieldFormData[]) => Promise<void>;
  refreshFields: () => Promise<void>;
}

/**
 * Custom hook to manage field creation and updates.
 * Handles creating vmeta fields and refreshing the field list.
 */
export function useFieldManagement({
  selectedVmetaProject,
  collectionId,
}: UseFieldManagementOptions): UseFieldManagementReturn {
  const { mutateAsync: createVmetaField } = useCreateVmetaField();
  const { mutateAsync: getProjectDatasetFields } = useGetProjectDatasetFields();
  const { setNotification } = useNotifications();

  const refreshFields = useCallback(async () => {
    if (!selectedVmetaProject?.value) {
      throw new Error("No project selected");
    }

    await getProjectDatasetFields({
      canvasId: selectedVmetaProject.value as string,
      collectionName: "file",
    });
  }, [selectedVmetaProject, getProjectDatasetFields]);

  const createFields = useCallback(
    async (fieldsToCreate: FieldFormData[]) => {
      if (!selectedVmetaProject?.value) {
        throw new Error("No project selected");
      }

      if (!collectionId) {
        throw new Error("No collection ID provided");
      }

      if (fieldsToCreate.length === 0) {
        return;
      }

      try {
        const createPromises = fieldsToCreate.map(fieldData =>
          createVmetaField({
            name: fieldData.name,
            label: fieldData.label,
            type: fieldData.type as any,
            collectionID: collectionId,
            unique: fieldData.unique,
            required: fieldData.required,
            perms: [],
          })
        );

        await Promise.all(createPromises);
        await refreshFields();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create fields";

        setNotification({
          id: "field-creation-error",
          message: "Failed to create fields",
          details: errorMessage,
          variant: NOTIFICATION.ERROR,
        });

        throw error;
      }
    },
    [
      selectedVmetaProject,
      collectionId,
      createVmetaField,
      refreshFields,
      setNotification,
    ]
  );

  return {
    createFields,
    refreshFields,
  };
}
